import React, { useState, useEffect } from 'react';
import Icon from '../../components/Icon';

function RecordRow({ label, record, isNew }) {
  if (!record || !record.manager) return null;
  return (
    <div className="lor-record-row">
      <div className="lor-record-label">{label}</div>
      <div className="lor-record-value">
        <span className="lor-pf" style={{ fontWeight: 700, fontSize: '1.05rem' }}>
          {typeof record.value === 'number' && record.value % 1 !== 0
            ? record.value.toFixed(2)
            : record.value}
        </span>
        {isNew && <span className="lor-record-new-badge">New</span>}
      </div>
      <div className="lor-record-holder">
        <span className="lor-manager-name">{record.manager}</span>
        {record.season && (
          <span className="lor-record-season"> · {record.season}</span>
        )}
        {record.week && (
          <span className="lor-record-season"> Wk {record.week}</span>
        )}
        {record.note && (
          <span className="lor-record-season"> · {record.note}</span>
        )}
      </div>
    </div>
  );
}

function RecordSection({ title, children }) {
  return (
    <div className="lor-record-section lor-card">
      <div className="lor-record-section-title">{title}</div>
      {children}
    </div>
  );
}

export default function LORRecords({ api }) {
  const [records, setRecords] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    fetch(`${api}/api/lor/records`)
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(data => { setRecords(data); setLoading(false); })
      .catch(() => { setError('Record book unavailable.'); setLoading(false); });
  }, [api]);

  if (loading) return <div className="lor-loading">Loading record book…</div>;
  if (error)   return <div className="lor-empty">{error}</div>;
  if (!records) return <div className="lor-empty">No records available yet.</div>;

  const sw = records.single_week || {};
  const sr = records.season      || {};
  const at = records.all_time    || {};

  return (
    <div>
      <div className="lor-page-header">
        <h2 className="lor-page-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon emoji="📋" size={18} /> Record Book</h2>
        <div className="lor-page-meta">All-time League of Record records</div>
      </div>

      <RecordSection title={<><Icon emoji="⚡" size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Single Week Records</>}>
        <RecordRow label="Highest single-week score"       record={sw.highest_score} />
        <RecordRow label="Lowest score (non-zero)"         record={sw.lowest_score_nonzero} />
        <RecordRow label="Highest losing score"            record={sw.highest_losing_score} />
        <RecordRow label="Biggest margin of victory"       record={sw.biggest_margin} />
      </RecordSection>

      <RecordSection title={<><Icon emoji="📅" size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Season Records</>}>
        <RecordRow label="Highest season points total"     record={sr.highest_pf} />
        <RecordRow label="Lowest season points total"      record={sr.lowest_pf} />
        <RecordRow label="Most wins in a season"           record={sr.most_wins} />
        <RecordRow label="Fewest wins in a season"         record={sr.fewest_wins} />
        <RecordRow label="Best win percentage"             record={sr.best_win_pct} />
        <RecordRow label="Worst win percentage"            record={sr.worst_win_pct} />
      </RecordSection>

      <RecordSection title={<><Icon emoji="🏆" size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> All-Time Records</>}>
        <RecordRow label="Most championships"              record={at.most_championships} />
        <RecordRow label="Most finals appearances"         record={at.most_finals} />
        <RecordRow label="Most playoff appearances"        record={at.most_playoffs} />
        <RecordRow label="Most regular season wins"        record={at.most_wins} />
        <RecordRow label="Most points scored all-time"     record={at.most_points} />
        <RecordRow label="Best all-time win % (min 3 seasons)" record={at.best_win_pct} />
        <RecordRow label="Longest winning streak"         record={at.longest_win_streak} />
        <RecordRow label="Longest losing streak"          record={at.longest_loss_streak} />
      </RecordSection>

      <div className="lor-history-note">
        Records compiled from 2016–present. Sleeper data auto-synced. Pre-2022 records from commissioner entries.
      </div>
    </div>
  );
}
